import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Heart, LogOut, Loader2, Calendar, ShoppingBag, Clock, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { myOrders } from "../lib/api";
import ProductCard from "../components/ProductCard";
import SchedulePicker from "../components/SchedulePicker";

const STATUS_LABEL = {
  new: "Received",
  confirmed: "Confirmed",
  picked_up: "Picked up",
  completed: "Completed",
};

const Account = () => {
  const { user, loading, signOut } = useAuth();
  const { ids, reload } = useWishlist();
  const [wishProducts, setWishProducts] = useState([]);
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [reschedule, setReschedule] = useState(null);

  const loadOrders = () => myOrders().then(setOrders).catch(() => setOrders([])).finally(() => setLoadingOrders(false));

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) loadOrders();
  }, [user]);

  // fetch full wishlist products
  useEffect(() => {
    if (!user) return;
    import("../lib/api").then(({ getWishlist }) =>
      getWishlist().then((r) => setWishProducts(r.products || [])).catch(() => {})
    );
  }, [user, ids.length]);

  const doLogout = async () => { await signOut(); navigate("/"); };

  if (loading || !user) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 size={36} className="text-[#0e2a1e] animate-spin" />
      </div>
    );
  }

  return (
    <main className="max-w-[1100px] mx-auto px-6 mt-10 min-h-[55vh] fade-up">
      {/* Header card */}
      <div className="bg-[#0e2a1e] text-[#f4f2ea] rounded-2xl p-6 flex items-center gap-4">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[#a4d61e]" />
        ) : (
          <span className="w-16 h-16 rounded-full bg-[#a4d61e] text-[#0e2a1e] flex items-center justify-center text-2xl font-bold">
            {(user.name || user.email).charAt(0).toUpperCase()}
          </span>
        )}
        <div className="flex-1">
          <h1 className="font-serif-brand text-2xl font-semibold">{user.name || "My account"}</h1>
          <p className="text-[#f4f2ea]/70 text-sm">{user.email}</p>
        </div>
        <button onClick={doLogout} className="flex items-center gap-2 text-sm bg-[#f4f2ea]/10 hover:bg-[#f4f2ea]/20 px-3 py-1.5 rounded-full">
          <LogOut size={16} /> Logout
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mt-6 mb-8">
        <button onClick={() => setTab("orders")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === "orders" ? "bg-[#0e2a1e] text-[#f4f2ea]" : "bg-white text-[#0e2a1e] border border-[#0e2a1e]/10"}`}>
          <Package size={16} /> Orders &amp; Bookings
        </button>
        <button onClick={() => setTab("wishlist")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === "wishlist" ? "bg-[#0e2a1e] text-[#f4f2ea]" : "bg-white text-[#0e2a1e] border border-[#0e2a1e]/10"}`}>
          <Heart size={16} /> Wishlist
        </button>
      </div>

      {/* Orders */}
      {tab === "orders" && (
        <div>
          {loadingOrders ? (
            <Loader2 size={28} className="text-[#0e2a1e] animate-spin" />
          ) : orders.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag size={40} className="text-[#0e2a1e]/30 mx-auto" />
              <p className="text-[#0e2a1e]/60 mt-4">No orders or bookings yet.</p>
              <Link to="/shop" className="mt-6 inline-block bg-[#a4d61e] text-[#0e2a1e] font-semibold px-6 py-3 rounded-full hover:bg-[#b6e63a] transition-colors">Start shopping</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${o.kind === "booking" ? "bg-[#a4d61e]/25 text-[#2f6b1e]" : "bg-[#0e2a1e]/10 text-[#0e2a1e]"}`}>
                        {o.kind === "booking" ? "Booking" : "Order"}
                      </span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span>
                      <span className="text-[11px] text-[#0e2a1e]/50 flex items-center gap-1"><Calendar size={12} /> {new Date(o.created_at).toLocaleDateString()}</span>
                    </div>
                    <span className="text-xs font-medium text-[#2f6b1e]">{STATUS_LABEL[o.fulfillment_status] || o.fulfillment_status}</span>
                  </div>
                  <div className="mt-3 border-t border-[#0e2a1e]/5 pt-3 text-sm text-[#0e2a1e]/75">
                    {o.items?.map((it, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{it.name}{it.variant ? ` — ${it.variant}` : ""} × {it.qty}</span>
                        <span>${(it.price * it.qty).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-right font-semibold text-[#0e2a1e]">${o.amount?.toFixed(2)} CAD</div>
                  {o.payment_status === "paid" && o.kind !== "donation" && (
                    <div className="mt-3 border-t border-[#0e2a1e]/5 pt-3 flex flex-wrap items-center justify-between gap-2">
                      {o.schedule?.date ? (
                        <span className="text-sm text-[#2f6b1e] font-medium flex items-center gap-1.5">
                          <Clock size={14} /> {o.kind === "booking" ? "Session" : "Pick-up"}: {o.schedule.date} · {o.schedule.time}
                        </span>
                      ) : (
                        <span className="text-sm text-amber-700 flex items-center gap-1.5">
                          <Clock size={14} /> No {o.kind === "booking" ? "session" : "pick-up"} time chosen yet
                        </span>
                      )}
                      <button
                        onClick={() => setReschedule(o)}
                        data-testid={`reschedule-btn-${o.id}`}
                        className="text-sm font-semibold text-[#0e2a1e] border border-[#0e2a1e]/20 hover:border-[#0e2a1e]/50 rounded-full px-4 py-1.5 transition-colors"
                      >
                        {o.schedule?.date ? "Reschedule" : "Schedule now"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reschedule modal */}
      {reschedule && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="reschedule-modal">
          <div className="bg-[#f4f2ea] rounded-2xl w-full max-w-lg p-6 my-auto relative">
            <button onClick={() => setReschedule(null)} data-testid="reschedule-close-btn" className="absolute top-4 right-4 text-[#0e2a1e]/50 hover:text-[#0e2a1e]"><X size={22} /></button>
            <SchedulePicker
              sessionId={reschedule.session_id}
              kind={reschedule.kind === "booking" ? "booking" : "order"}
              onScheduled={() => { setReschedule(null); loadOrders(); }}
            />
          </div>
        </div>
      )}

      {/* Wishlist */}
      {tab === "wishlist" && (
        <div>
          {wishProducts.length === 0 ? (
            <div className="text-center py-16">
              <Heart size={40} className="text-[#0e2a1e]/30 mx-auto" />
              <p className="text-[#0e2a1e]/60 mt-4">Your wishlist is empty. Tap the heart on any product to save it.</p>
              <Link to="/shop" className="mt-6 inline-block bg-[#a4d61e] text-[#0e2a1e] font-semibold px-6 py-3 rounded-full hover:bg-[#b6e63a] transition-colors">Browse products</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {wishProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default Account;
